import { useState } from "react";
import { Paperclip, Lock } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { FilePicker, FilePreviewDialog } from "@/components/common/Files";
import { fmtDateTime } from "@/lib/format";

function Message({ m, viewer, onOpen }) {
  if (m.author_role === "system") {
    return <p className="text-center text-xs text-warm-text italic py-2">{m.body} · {fmtDateTime(m.created_at)}</p>;
  }
  const mine = (viewer === "center" && m.author_role === "center") || (viewer === "staff" && m.author_role === "staff");
  const who = m.author_role === "center" ? (viewer === "center" ? "Tu" : m.author_name) : viewer === "center" ? "Team Skin Lovers" : m.author_name;
  const tone = m.internal
    ? "border border-dashed border-gold/60 bg-gold/[0.06]"
    : mine ? "bg-gradient-to-br from-gold/25 to-gold/[0.08] border border-gold/40 text-white" : "bg-ivory-deep border border-white/[0.08]";
  return (
    <div data-testid={`ticket-message-${m.id}`} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[88%] sm:max-w-[75%] px-5 py-4 ${tone}`}>
        <div className={`flex items-center gap-2 text-[11px] uppercase tracking-[0.14em] mb-2 ${mine && !m.internal ? "text-gold-light/80" : "text-warm-text"}`}>
          {m.internal && <Lock className="w-3 h-3" />}
          <span>{m.internal ? `Nota interna · ${who}` : who}</span>
          <span>·</span>
          <span className="normal-case tracking-normal">{fmtDateTime(m.created_at)}</span>
        </div>
        {m.body && <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{m.body}</p>}
        {m.attachments?.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {m.attachments.map((a) => (
              <button key={a.id} data-testid={`attachment-${a.id}`} onClick={() => onOpen(a)}
                className={`inline-flex items-center gap-1.5 text-xs underline underline-offset-4 ${mine && !m.internal ? "text-gold-light" : "text-gold"}`}>
                <Paperclip className="w-3 h-3" /> <span className="max-w-[160px] truncate">{a.filename}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TicketThread({ messages, viewer }) {
  const [file, setFile] = useState(null);
  return (
    <div data-testid="ticket-thread" className="space-y-4">
      {messages.map((m) => <Message key={m.id} m={m} viewer={viewer} onOpen={setFile} />)}
      <FilePreviewDialog kind="attachment" file={file} onClose={() => setFile(null)} />
    </div>
  );
}

export function ReplyBox({ onSend, allowInternal, placeholder = "Scrivi la tua risposta…" }) {
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!body.trim() && !files.length) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("body", body);
    if (allowInternal) fd.append("internal", internal ? "true" : "false");
    files.forEach((f) => fd.append("files", f));
    const ok = await onSend(fd);
    setBusy(false);
    if (ok) {
      setBody("");
      setFiles([]);
      setInternal(false);
    }
  };
  return (
    <form onSubmit={submit} className={`lux-card p-5 sm:p-6 space-y-4 ${internal ? "border-dashed border-gold/60" : ""}`}>
      <Textarea data-testid="reply-textarea" value={body} onChange={(e) => setBody(e.target.value)} rows={4}
        placeholder={internal ? "Nota visibile solo allo staff…" : placeholder} className="bg-ivory-deep rounded-xl" />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-5">
          <FilePicker files={files} setFiles={setFiles} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" testid="reply-files" />
          {allowInternal && (
            <label className="inline-flex items-center gap-2 text-xs text-warm-text">
              <Switch data-testid="reply-internal-switch" checked={internal} onCheckedChange={setInternal} /> Nota interna
            </label>
          )}
        </div>
        <button data-testid="reply-submit-btn" type="submit" disabled={busy || (!body.trim() && !files.length)} className="btn-gold">
          {busy ? "Invio…" : internal ? "Salva nota" : "Invia"}
        </button>
      </div>
    </form>
  );
}
